import type { ReactNode } from "react";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";

const tones = {
  info: { box: "border-info/25 bg-info/[0.06]", icon: "text-info", name: "info" },
  warning: { box: "border-warning/25 bg-warning/[0.06]", icon: "text-warning", name: "alertTriangle" },
  success: { box: "border-success/25 bg-success/[0.06]", icon: "text-success", name: "checkCircle" },
  danger: { box: "border-danger/25 bg-danger/[0.06]", icon: "text-danger", name: "alertCircle" },
} as const;

/**
 * A short message above the item: why a phrase must be recorded again, a
 * resumed session, a blocked account...
 *
 * `<TaskNotice tone="info" title="Réenregistrer cette phrase">Motif : bruit de fond</TaskNotice>`
 */
export function TaskNotice({
  tone = "info",
  title,
  children,
  icon,
  className,
}: {
  tone?: keyof typeof tones;
  title: ReactNode;
  children?: ReactNode;
  icon?: IconName;
  className?: string;
}) {
  const style = tones[tone];
  return (
    <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3", style.box, className)}>
      <Icon name={icon ?? style.name} className={cn("mt-0.5 size-4 shrink-0", style.icon)} />
      <div className="min-w-0 space-y-1 text-sm">
        <p className="font-medium text-foreground">{title}</p>
        {children ? <div className="leading-6 text-muted-foreground">{children}</div> : null}
      </div>
    </div>
  );
}
